// src/components/Casos.js
// Aba dos casos de Planejamento Conflitual (planilha consolidada do projeto
// do Guilherme): mapa do Brasil, busca, filtro por tipologia e ficha do caso.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Carregando from "./Carregando";
import PainelDetalhe, { Campo } from "./PainelDetalhe";
import ListaFiltro from "./ListaFiltro";
import { COR_DADO, COR_TINTA, TILE_URL, buscarCasos } from "./Mapa";

// Nome curto de cada tipologia da planilha, para a lista e as etiquetas.
export const TIPOLOGIAS_NOMES = {
  "Conflito fundiário (ações possessórias)": "Conflito fundiário",
  "Ameaça de remoção/despejo": "Remoção e despejo",
  "Intervenções urbanas no entorno": "Intervenções no entorno",
  "Obras de urbanização no assentamento": "Urbanização do assentamento",
  "Ameaças a povos e comunidades tradicionais (Deslocamento, despossessão, falta de reconhecimento)":
    "Povos e comunidades tradicionais",
  "Lutas dos povos do campo, das águas e das florestas (Reforma agrária)": "Campo, águas e florestas",
  "Desastres ambientais": "Desastres ambientais",
  "Crimes ambientais": "Crimes ambientais",
  Outros: "Outros",
};

const PROFUNDIDADE = {
  alta: "Registro aprofundado",
  media: "Registro parcial",
  baixa: "Registro inicial",
};

const SUGESTOES_CASO = [
  "Resuma este caso em poucas palavras",
  "Quem se mobilizou e contra quem?",
  "Que ações de planejamento foram feitas?",
  { texto: "Como está a situação hoje?", internet: true },
];

const LARGURA_PAINEL = 440;

function nomeTipologia(tipologia) {
  return TIPOLOGIAS_NOMES[tipologia] || tipologia;
}

function ehNacional(caso) {
  return Boolean(caso.uf && caso.uf.length === 2);
}

function normalizar(texto) {
  return (texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function formatarData(iso) {
  if (!iso) return null;
  const [ano, mes] = String(iso).split("-");
  return mes ? `${mes}/${ano}` : ano;
}

function localDoCaso(caso) {
  const cidade = [caso.municipio, ehNacional(caso) ? caso.uf : null].filter(Boolean).join(", ");
  return cidade || caso.localizacao || "Local não informado";
}

function codigoDoEndereco() {
  const [aba, codigo] = window.location.hash.slice(1).split("/");
  return aba === "casos" && codigo ? decodeURIComponent(codigo) : null;
}

// Leva o caso escolhido para a parte do mapa que o painel não cobre.
function Enquadrar({ caso }) {
  const mapa = useMap();
  useEffect(() => {
    if (!caso || caso.latitude == null) return;
    const ponto = L.latLng(caso.latitude, caso.longitude);
    const painelAoLado = window.innerWidth > 768;
    mapa.flyToBounds(L.latLngBounds(ponto, ponto), {
      paddingBottomRight: [painelAoLado ? LARGURA_PAINEL : 0, 0],
      maxZoom: Math.max(mapa.getZoom(), 9),
      duration: 0.8,
    });
  }, [caso, mapa]);
  return null;
}

// Na primeira carga, enquadra os casos do Brasil (os poucos internacionais
// afastariam demais o mapa).
function EnquadrarTodos({ casos, ativo }) {
  const mapa = useMap();
  const [feito, setFeito] = useState(false);
  useEffect(() => {
    const nacionais = casos.filter(ehNacional);
    if (feito || !ativo || nacionais.length === 0) return;
    mapa.fitBounds(L.latLngBounds(nacionais.map((c) => [c.latitude, c.longitude])), { padding: [24, 24] });
    setFeito(true);
  }, [ativo, casos, feito, mapa]);
  return null;
}

function FichaCaso({ caso }) {
  const ficha = caso.ficha_ia || {};
  const periodo = [formatarData(caso.data_inicio), formatarData(caso.data_fim)].filter(Boolean).join(" – ");
  const temRegistro = (caso.secoes || []).length > 0;

  return (
    <>
      <div className="ficha-etiquetas">
        {(caso.tipologias || []).map((t) => (
          <span key={t} className="ficha-etiqueta">{nomeTipologia(t)}</span>
        ))}
        {caso.profundidade && (
          <span className={`ficha-profundidade ficha-profundidade-${caso.profundidade}`}>
            {PROFUNDIDADE[caso.profundidade]}
          </span>
        )}
      </div>

      {ficha.em_uma_frase && <p className="ficha-destaque">{ficha.em_uma_frase}</p>}

      {caso.resumo && (
        <section className="ficha-bloco">
          <h3>Resumo</h3>
          <p>{caso.resumo}</p>
        </section>
      )}

      {ficha.sintese && (
        <section className="ficha-bloco">
          <h3>Síntese <span className="ficha-selo-ia">organizada por IA</span></h3>
          <p>{ficha.sintese}</p>
        </section>
      )}

      <dl className="ficha-campos">
        <Campo rotulo="Quem se mobiliza">{ficha.quem_se_mobiliza}</Campo>
        <Campo rotulo="Opositores">{ficha.opositores}</Campo>
        <Campo rotulo="Apoiadores" largo>{ficha.apoiadores}</Campo>
        <Campo rotulo="Período da assessoria">{periodo}</Campo>
        <Campo rotulo="Urgência">{caso.emergencialidade}</Campo>
        <Campo rotulo="Temporalidade" largo>{caso.temporalidade}</Campo>
        <Campo rotulo="Local" largo>{caso.localizacao}</Campo>
      </dl>

      {(ficha.linha_do_tempo || []).length > 0 && (
        <section className="ficha-bloco">
          <h3>Linha do tempo</h3>
          <ol className="ficha-tempo">
            {ficha.linha_do_tempo.map((marco, i) => (
              <li key={i}>
                <span className="ficha-tempo-quando">{marco.quando}</span>
                <span>{marco.o_que}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {(caso.praticas || []).length > 0 && (
        <section className="ficha-bloco">
          <h3>Práticas de planejamento e assessoria</h3>
          <ul className="ficha-praticas">
            {caso.praticas.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </section>
      )}

      {ficha.resultados && (
        <section className="ficha-bloco">
          <h3>Resultados</h3>
          <p>{ficha.resultados}</p>
        </section>
      )}

      {temRegistro && (
        <section className="ficha-bloco">
          <h3>Registro completo da pesquisa</h3>
          <div className="ficha-registro">
            {caso.secoes.map((secao) => (
              <details key={secao.grupo}>
                <summary>
                  {secao.grupo}
                  <span>{secao.campos.length}</span>
                </summary>
                {secao.campos.map((campo) => (
                  <div key={campo.rotulo} className="ficha-registro-campo">
                    <h4>{campo.rotulo}</h4>
                    <p>{campo.texto}</p>
                  </div>
                ))}
              </details>
            ))}
          </div>
        </section>
      )}

      {!temRegistro && !caso.resumo && !ficha.sintese && (
        <p className="ficha-vazia">
          Este caso ainda tem poucas informações registradas. A pesquisa segue
          levantando dados sobre ele.
        </p>
      )}

      {(caso.links || []).length > 0 && (
        <section className="ficha-bloco">
          <h3>Para saber mais</h3>
          <ul className="ficha-links">
            {caso.links.map((url) => (
              <li key={url}>
                <a href={url} target="_blank" rel="noreferrer">{url.replace(/^https?:\/\/(www\.)?/, "")}</a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(ficha.lacunas || []).length > 0 && (
        <section className="ficha-bloco ficha-lacunas">
          <h3>O que ainda falta saber</h3>
          <ul>
            {ficha.lacunas.map((l) => <li key={l}>{l}</li>)}
          </ul>
        </section>
      )}

      <p className="ficha-fonte">
        {caso.codigo}
        {caso.fonte && ` · Fonte: ${caso.fonte}`}
        {caso.localizacao_aproximada && " · Posição aproximada (centro do município)"}
      </p>
    </>
  );
}

function Casos() {
  const [casos, setCasos] = useState(null);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState("");
  const [tipologia, setTipologia] = useState("todos");
  const [codigoAberto, setCodigoAberto] = useState(codigoDoEndereco);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  useEffect(() => {
    buscarCasos()
      .then((dados) => setCasos(Array.isArray(dados) ? dados : []))
      .catch((e) => {
        console.error("Erro ao carregar casos:", e);
        setErro(true);
        setCasos([]);
      });
  }, []);

  useEffect(() => {
    const aoMudar = () => setCodigoAberto(codigoDoEndereco());
    window.addEventListener("hashchange", aoMudar);
    return () => window.removeEventListener("hashchange", aoMudar);
  }, []);

  // O caso aberto fica no endereço para que o link possa ser compartilhado.
  const abrir = useCallback((codigo) => {
    setCodigoAberto(codigo);
    setFiltrosAbertos(false);
    window.history.replaceState(null, "", codigo ? `#casos/${encodeURIComponent(codigo)}` : "#casos");
  }, []);
  const fechar = useCallback(() => abrir(null), [abrir]);

  // A busca vale antes da tipologia, para que as contagens da lista mostrem
  // quantos casos de cada tipo a busca encontrou.
  const buscados = useMemo(() => {
    const termo = normalizar(busca.trim());
    return (casos || []).filter((c) => !termo || normalizar([
      c.titulo, c.titulo_alternativo, c.municipio, c.uf, c.localizacao,
      c.resumo, ...(c.tipologias || []), ...(c.praticas || []),
    ].join(" ")).includes(termo));
  }, [casos, busca]);

  const opcoesTipologia = useMemo(() => {
    const contagem = {};
    buscados.forEach((c) => (c.tipologias || []).forEach((t) => {
      contagem[t] = (contagem[t] || 0) + 1;
    }));
    return Object.entries(contagem)
      .sort((a, b) => b[1] - a[1])
      .map(([id, total]) => ({ id, nome: nomeTipologia(id), total }));
  }, [buscados]);

  const filtrados = useMemo(() => (
    buscados
      .filter((c) => tipologia === "todos" || (c.tipologias || []).includes(tipologia))
      .sort((a, b) => (a.titulo_alternativo || a.titulo).localeCompare(b.titulo_alternativo || b.titulo, "pt-BR"))
  ), [buscados, tipologia]);

  const casoAberto = useMemo(
    () => (casos || []).find((c) => c.codigo === codigoAberto) || null,
    [casos, codigoAberto],
  );
  const noMapa = filtrados.filter((c) => c.latitude != null && c.longitude != null);
  const aproximados = noMapa.filter((c) => c.localizacao_aproximada).length;

  const todos = casos || [];
  const ufs = new Set(todos.filter(ehNacional).map((c) => c.uf)).size;
  const foraDoBrasil = todos.filter((c) => !ehNacional(c)).length;

  return (
    <>
      {filtrosAbertos && (
        <div className="sidebar-fundo" onClick={() => setFiltrosAbertos(false)} aria-hidden="true" />
      )}
      <aside id="filtros-casos" className={`sidebar ${filtrosAbertos ? "open" : ""}`}>
        <div className="lateral-topo">
          <h1 className="lateral-titulo">Experiências de planejamento</h1>
          <p className="lateral-linha">
            {casos === null
              ? "Carregando as experiências…"
              : `${todos.length} experiências de planejamento e assessoria popular em conflitos, em ${ufs} estados${foraDoBrasil ? ` e ${foraDoBrasil} fora do Brasil` : ""}.`}
          </p>
          <input
            type="search"
            className="campo-busca"
            placeholder="Buscar por nome, cidade ou tema"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            aria-label="Buscar experiências"
          />
        </div>

        <ListaFiltro
          titulo="Tipologia do conflito"
          rotuloTodas="Todas as tipologias"
          total={buscados.length}
          opcoes={opcoesTipologia}
          selecionada={tipologia}
          aoEscolher={setTipologia}
        />

        <section className="resultados" aria-label="Experiências encontradas">
          <h2 className="lateral-secao">
            {filtrados.length} {filtrados.length === 1 ? "experiência" : "experiências"}
            <span className="lateral-secao-nota">de A a Z</span>
          </h2>
          {casos === null && <Carregando texto="Carregando as experiências…" />}
          {erro && <p className="lateral-aviso">Não foi possível carregar as experiências agora. Tente recarregar a página.</p>}
          {casos !== null && !erro && filtrados.length === 0 && (
            <p className="lateral-aviso">Nenhuma experiência encontrada com essa busca.</p>
          )}
          <ul className="registro-lista">
            {filtrados.map((caso) => (
              <li key={caso.codigo}>
                <button
                  type="button"
                  className={`registro-item ${caso.codigo === codigoAberto ? "ativo" : ""}`}
                  aria-current={caso.codigo === codigoAberto ? "true" : undefined}
                  onClick={() => abrir(caso.codigo)}
                >
                  <span className="registro-titulo">{caso.titulo_alternativo || caso.titulo}</span>
                  <span className="registro-meta">
                    {localDoCaso(caso)}
                    {caso.tipologias?.[0] && ` · ${nomeTipologia(caso.tipologias[0])}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </aside>

      <div className="map-area">
        <button
          type="button"
          className="botao-filtros"
          onClick={() => setFiltrosAbertos(true)}
          aria-controls="filtros-casos"
          aria-expanded={filtrosAbertos}
        >
          Lista e filtros · {filtrados.length}
        </button>
        {casos === null && (
          <div className="loading-overlay">
            <Carregando texto="Carregando as experiências…" />
          </div>
        )}

        <MapContainer center={[-15.5, -52]} zoom={4} minZoom={3} className="map-container">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url={TILE_URL}
          />
          <Enquadrar caso={casoAberto} />
          <EnquadrarTodos casos={noMapa} ativo={casos !== null && !codigoAberto} />
          {noMapa.map((caso) => {
            const aberto = caso.codigo === codigoAberto;
            const aproximado = caso.localizacao_aproximada;
            return (
              <CircleMarker
                key={`${caso.codigo}:${aberto}`}
                center={[caso.latitude, caso.longitude]}
                radius={aberto ? 10 : 6}
                pathOptions={{
                  color: aberto ? COR_TINTA : aproximado ? COR_DADO : "#ffffff",
                  weight: aberto ? 3 : aproximado ? 2 : 1.5,
                  fillColor: aproximado && !aberto ? "#ffffff" : COR_DADO,
                  fillOpacity: 1,
                }}
                eventHandlers={{ click: () => abrir(caso.codigo) }}
              >
                <Tooltip direction="top" offset={[0, -6]}>
                  <strong>{caso.titulo_alternativo || caso.titulo}</strong>
                  <br />
                  {localDoCaso(caso)}
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {casos !== null && noMapa.length > 0 && (
          <div className="mapa-legenda" aria-label="Legenda do mapa">
            <span><i className="legenda-ponto" aria-hidden="true" /> Local registrado <b className="num">{noMapa.length - aproximados}</b></span>
            <span><i className="legenda-ponto legenda-ponto-vazado" aria-hidden="true" /> Centro do município <b className="num">{aproximados}</b></span>
          </div>
        )}

        {casoAberto && (
          <PainelDetalhe
            rotulo={`Experiência · ${localDoCaso(casoAberto)}`}
            titulo={casoAberto.titulo_alternativo || casoAberto.titulo}
            subtitulo={casoAberto.titulo_alternativo ? casoAberto.titulo : null}
            tipo="caso"
            id={casoAberto.codigo}
            sugestoes={SUGESTOES_CASO}
            aoFechar={fechar}
          >
            <FichaCaso caso={casoAberto} />
          </PainelDetalhe>
        )}
      </div>
    </>
  );
}

export default Casos;
